import { inject, Injectable, Injector, Type } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, UrlTree } from '@angular/router';
import { BdAuthenticationService } from '@bindoc-projects/security-lib/data';
import { DialogService } from 'primeng/dynamicdialog';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { BdAdminPermission } from '../admin/core/admin-permission.const';
import { BdAdminPermissionService } from '../admin/core/admin-permissions.service';
import { BdFeaturePermissionService } from '../features/core';
import {
  BdPermissionGuardDialogComponent,
  BdPermissionGuardDialogData,
} from './dialog/dialog.component';
import {
  BD_PERMISSION_FORBIDDEN_PATH,
  BdPermissionFailStrategy,
} from './guard.const';

export interface BdFeaturePermissionGuardData {
  permissions: `${string}:${string}`[];
  failStrategy: BdPermissionFailStrategy;
}

export interface BdAdminPermissionGuardData {
  permissions: BdAdminPermission[];
  failStrategy: BdPermissionFailStrategy;
}

export interface BdPermissionGuardConfig {
  failStrategy: BdPermissionFailStrategy;
  feature?: `${string}:${string}`[];
  admin?: BdAdminPermission[];
}

export interface BdPermissionGuardRouteData {
  featurePermissionsData?: BdFeaturePermissionGuardData;
  adminPermissionsData?: BdAdminPermissionGuardData;
}

@Injectable({
  providedIn: 'root',
})
export class BdPermissionGuard implements CanActivate {
  private readonly _injector = inject(Injector);
  private readonly _featurePermissionService = inject(
    BdFeaturePermissionService,
  );
  private readonly _adminPermissionService = inject(BdAdminPermissionService);
  private readonly _authService = inject(BdAuthenticationService);

  /**
   * Builds the route `data` payload from a single config so the route
   * declaration stays terse and `failStrategy` is only stated once.
   *
   * Mirrors `BdPageTitleService.title('Kunden')` — call it inline:
   *
   * ```ts
   * data: {
   *   ...BdPermissionGuard.permissions({
   *     failStrategy: BdPermissionFailStrategy.DIALOG,
   *     admin: ['ADMIN:FEATURE'],
   *   }),
   *   pageTitle: BdPageTitleService.title('Kunden'),
   * }
   * ```
   */
  public static permissions(
    config: BdPermissionGuardConfig,
  ): BdPermissionGuardRouteData {
    const data: BdPermissionGuardRouteData = {};
    if (config.feature && config.feature.length > 0) {
      data.featurePermissionsData = {
        permissions: config.feature,
        failStrategy: config.failStrategy,
      };
    }
    if (config.admin && config.admin.length > 0) {
      data.adminPermissionsData = {
        permissions: config.admin,
        failStrategy: config.failStrategy,
      };
    }
    return data;
  }

  canActivate(
    route: ActivatedRouteSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const featureData = route.data['featurePermissionsData'] as
      BdFeaturePermissionGuardData | undefined;
    const adminData = route.data['adminPermissionsData'] as
      BdAdminPermissionGuardData | undefined;

    if (!featureData && !adminData) {
      return true;
    }

    return this._authService.msalInitialized.pipe(
      take(1),
      // Wait until MSAL actually knows an authenticated account before firing
      // permission API calls. Angular evaluates a route's canActivate guards
      // CONCURRENTLY, and every BdPermissionGuard route pairs it with
      // MsalGuard: while unauthenticated, MsalGuard owns the interaction —
      // probing the API here would make the MsalInterceptor race a second
      // acquireTokenRedirect against MsalGuard's loginRedirect
      // (interaction_in_progress → visible loginFailedRoute flash, duplicate
      // B2C authorize navigations). When the login redirect proceeds, the
      // page unloads and this pending observable dies with it.
      switchMap(() => this._authService.authentication$),
      filter((authenticated) => authenticated),
      take(1),
      switchMap(() =>
        combineLatest([
          featureData
            ? this._featurePermissionService.containsPermission(
                featureData.permissions,
              )
            : of(true),
          adminData
            ? this._adminPermissionService.containsPermission(
                adminData.permissions,
              )
            : of(true),
        ]),
      ),
      map(([hasFeature, hasAdmin]) => {
        if (hasFeature && hasAdmin) {
          return true;
        }
        if (!hasFeature && featureData) {
          this._missingPermissionsNotification(
            featureData.failStrategy,
            'Sie haben nicht die erforderten Berechtigungen !',
            featureData.permissions,
          );
        } else if (!hasAdmin && adminData) {
          this._missingPermissionsNotification(
            adminData.failStrategy,
            'Sie haben nicht die erforderten Admin-Berechtigungen !',
            adminData.permissions,
          );
        }
        return false;
      }),
    );
  }

  private _missingPermissionsNotification(
    failStrategy: BdPermissionFailStrategy,
    message: string,
    permissionsToCheck: readonly string[],
  ): void {
    if (failStrategy === BdPermissionFailStrategy.DIALOG) {
      this._openDialog(BdPermissionGuardDialogComponent, {
        message,
        permissionsToCheck: [...permissionsToCheck],
      });
    } else {
      window.location.href = BD_PERMISSION_FORBIDDEN_PATH;
    }
  }

  private _openDialog(
    component: Type<BdPermissionGuardDialogComponent>,
    data: BdPermissionGuardDialogData,
  ): void {
    const dialogService = this._injector.get(DialogService, null, {
      optional: true,
    });
    dialogService?.open(component, {
      header: 'Zugang gesperrt',
      data,
    });
  }
}
